/**
 * Der Stil des Veranstalter-Raums.
 *
 * Eine Datei für beide Seiten: die öffentliche Veranstalterseite (`/veranstalter/:id`) und den
 * Bereich zum Pflegen sind dieselbe Seite in zwei Zuständen. Zwei Stylesheets hießen zwei
 * Gestaltungen, die auseinanderlaufen — und genau das war der Grund für die Zusammenlegung.
 *
 * Als Stylesheet und nicht als Inline-Style, weil Hover, Tastaturfokus, Bewegung und die
 * Mobilansicht sich inline nicht ausdrücken lassen. Farben und Schriften sind die der
 * öffentlichen Seiten — hier wird nichts erfunden, nur anders angeordnet.
 */
export const FONT_HEAD = "'Archivo',sans-serif";
export const FONT_MONO = "'Space Mono',monospace";

/**
 * Farben und Flaechen der oeffentlichen Seiten — bewusst dieselben Werte wie in Profile.tsx und
 * OrganizerDetail.tsx statt der Admin-Variablen. Der Veranstalter-Bereich gehoert zur Seite, die
 * ein Vereinsmensch kennt, nicht zum Werkzeugkasten der Redaktion.
 */
export const PAGE_BG = '#F5F3F3';
export const INK = '#2C2C2C';
export const MUTED = '#9A9494';
export const BORDER = '#E5E1E1';
export const BERRY = '#922C57';
/**
 * Farbe der kleinen Versalien-Beschriftungen (Briefkopf, Reiter, Monatslinie, Tabellenkopf).
 *
 * Bewusst dunkler als [MUTED]: das ist 10–11px grosse Schrift in Grossbuchstaben mit weiter
 * Laufweite, und [MUTED] (#9A9494) kommt auf dem hellen Grund nur auf rund 3:1 — unter dem
 * Mindestkontrast fuer kleinen Text (WCAG AA, 4.5:1). Dieser Wert liegt bei etwa 5,7:1.
 * [MUTED] bleibt, wo laufender Text in normaler Groesse steht.
 */
export const LABEL = '#615C5C';

/** Zustandsfarben aus der bestehenden Seite (Profil-Chips, vorlaeufiger Termin). */
export const GREEN = '#1F8A5B';
export const AMBER = '#D99F1A';


/**
 * Der Stil des Bereichs.
 *
 * Als Stylesheet und nicht als Inline-Style, weil Hover, Tastaturfokus, Bewegung und die
 * Mobilansicht sich inline nicht ausdruecken lassen. Farben und Schriften sind die der
 * oeffentlichen Seiten — hier wird nichts erfunden, nur anders angeordnet.
 */
export const ORGANIZER_SPACE_CSS = `
.vb-eyebrow { font-family:${FONT_MONO}; font-size:11px; letter-spacing:.14em; text-transform:uppercase; color:${LABEL}; }
.vb-h1 { font-family:${FONT_HEAD}; font-weight:800; font-size:40px; letter-spacing:-.02em; color:${INK}; margin:6px 0 0; line-height:1.05; }

/* Auswahl des Vereins: unauffaellig, aber als Erstes im Blick. */
.vb-switch { display:inline-flex; align-items:center; gap:10px; background:#fff; border:1px solid ${BORDER};
  border-radius:999px; padding:6px 8px 6px 14px; }
.vb-switch select { border:0; background:transparent; font-family:inherit; font-size:14px; font-weight:700;
  color:${INK}; outline:none; cursor:pointer; padding:4px 6px; }

/* --- Naechste Veranstaltung: die eine Frage, die ein Verein an diese Seite hat. --- */
.vb-next { position:relative; background:#fff; border:1px solid ${BORDER}; border-radius:20px; padding:26px 28px;
  overflow:hidden; }
.vb-next::before { content:''; position:absolute; inset:0 auto 0 0; width:4px; background:${BERRY}; }
.vb-count { font-family:${FONT_HEAD}; font-weight:800; font-size:52px; line-height:.9; letter-spacing:-.03em; color:${BERRY}; }
.vb-count-unit { font-family:${FONT_MONO}; font-size:11px; letter-spacing:.14em; text-transform:uppercase; color:${MUTED}; margin-top:8px; }
.vb-next-title { font-family:${FONT_HEAD}; font-weight:800; font-size:24px; letter-spacing:-.01em; color:${INK}; margin:0; line-height:1.15; }

/* --- Die Saison-Linie: Streckenfuehrung durch das Jahr. --- */
.vb-season { position:relative; margin:0; padding:0; list-style:none; }
.vb-month { display:flex; align-items:center; gap:14px; margin:26px 0 10px; }
.vb-month:first-child { margin-top:6px; }
/* Keine feste Breite: die 52px stammten von „SEP 26"; seit der Monat ausgeschrieben ist, lief der
   Text über die Spalte hinaus und die Linie darunter durch die Jahreszahl. */
.vb-month-label { font-family:${FONT_MONO}; font-size:11px; letter-spacing:.14em; color:${LABEL};
  flex:0 0 auto; white-space:nowrap; }
.vb-month-rule { flex:1; height:1px; background:${BORDER}; }

.vb-row { position:relative; display:grid; grid-template-columns:52px 1fr; gap:14px; align-items:start;
  padding:14px 0 18px; }
/* Die Linie laeuft vom Knoten abwaerts zum naechsten. Sie beginnt AM Punkt, damit ueber dem
   ersten Eintrag eines Monats kein Stummel steht, und entfaellt am letzten: eine Strecke endet. */
.vb-row::before { content:''; position:absolute; left:25px; top:64px; bottom:-4px; width:2px;
  background:${BORDER}; border-radius:2px; }
.vb-row:last-child::before { display:none; }
.vb-node { position:relative; z-index:1; display:flex; flex-direction:column; align-items:center; padding-top:2px; }
.vb-dot { width:12px; height:12px; border-radius:50%; background:#fff; border:2px solid #C9C2C2; }
.vb-dot--next { background:${BERRY}; border-color:${BERRY}; box-shadow:0 0 0 4px rgba(146,44,87,.12); }
.vb-dot--past { background:${BORDER}; border-color:${BORDER}; }
/* Vergangene Zeilen zurueckgenommen, aber lesbar.
   Nur **eine Stufe**: Flaeche etwas blasser, und was sonst INK ist (Titel, Tageszahl), geht auf
   LABEL zurueck. Die schon gedaempften Teile (.vb-meta, .vb-routes, .vb-weekday) bleiben, wie
   sie sind -- sie weiter aufzuhellen hiesse, sie unter jede Lesbarkeit zu schieben.
   Nicht ueber opacity: das nimmt auch dem Text den Kontrast, und eine halbtransparente Zeile
   auf hellem Grund faellt unter 4,5:1.
   Gerechnet: LABEL #615C5C auf #FCFBFB = 6,4:1. */
.vb-row--past .vb-card { background:#F7F5F5; border-color:#E9E4E4; box-shadow:none; }
.vb-row--past .vb-title { color:${LABEL}; }
.vb-row--past .vb-day { color:${LABEL}; }
/* Kleiner und hohl: der Knoten einer Zeile, die vorbei ist. */
.vb-row--past .vb-dot { width:9px; height:9px; background:#fff; border:2px solid #D8D2D2; margin-top:2px; }

/* Der Schnitt zwischen Gewesenem und Kommendem.
   In einer durchgehenden Zeitachse ist "heute" die eine Stelle, die den Unterschied erklaert --
   und sie erklaert ihn besser als jede weitere Abstufung der Zeilen, weil sie sagt WARUM die
   einen blasser sind. Die Linie laeuft durch, die Kette bleibt also eine. */
.vb-today { position:relative; display:grid; grid-template-columns:52px 1fr; gap:14px;
  align-items:center; padding:6px 0 14px; }
.vb-today::before { content:''; position:absolute; left:25px; top:0; bottom:-4px; width:2px;
  background:${BORDER}; border-radius:2px; }
/* Ring, nicht gefuellt: direkt darunter sitzt der gefuellte Beerenpunkt des naechsten Events.
   Zwei gleiche Punkte uebereinander sagten dasselbe zweimal — der Ring markiert die Stelle, der
   gefuellte Punkt den Termin. */
.vb-today-dot { position:relative; z-index:1; justify-self:center; width:11px; height:11px;
  border-radius:50%; background:#fff; border:2px solid ${BERRY}; box-sizing:border-box; }
.vb-today-label { font-family:${FONT_MONO}; font-size:10px; letter-spacing:.16em;
  text-transform:uppercase; color:${BERRY}; display:flex; align-items:center; gap:10px; }
/* Die Linie rechts macht aus der Beschriftung eine Trennung, nicht nur ein Woertchen. */
.vb-today-label::after { content:''; flex:1; height:1px; background:linear-gradient(to right, #E3B9CB, transparent); }
.vb-day { font-family:${FONT_HEAD}; font-weight:800; font-size:19px; color:${INK}; margin-top:8px; line-height:1; }
.vb-weekday { font-family:${FONT_MONO}; font-size:10.5px; letter-spacing:.1em; color:${LABEL}; margin-top:4px; }

.vb-card { display:flex; flex-wrap:wrap; align-items:center; justify-content:space-between; gap:20px;
  background:#fff; border:1px solid ${BORDER}; border-radius:16px; padding:15px 18px;
  transition:border-color .18s, box-shadow .18s; }
.vb-card-main { min-width:min(240px,100%); flex:1; }
/* Die naechste Veranstaltung: derselbe Bau wie die anderen Zeilen, nur mit Gewicht. Ein eigener
   Kasten weiter oben haette denselben Termin zweimal auf die Seite gebracht. */
.vb-card--next { border-color:#DCC6D1; box-shadow:0 2px 18px -10px rgba(146,44,87,.4); }
/* Fruehere Auflage: dieselbe Zeile, aber nichts mehr zu tun. Sie tritt zurueck, statt sich als
   zweite Art von Karte auszugeben — das Wort in der Meta-Zeile sagt, was sie ist. */
.vb-card--edition { background:transparent; box-shadow:none; }
.vb-card--edition .vb-title { font-size:16px; color:${MUTED}; }
/* Angesprungen aus *Sichtbarkeit & Promotion*: der Rahmen beantwortet „wo bin ich gelandet?" und
   verschwindet nach ein paar Sekunden von selbst — die Karte behaelt ihre eigene Auszeichnung.
   Er nutzt den Schatten und nicht die Rahmenfarbe, damit die naechste Veranstaltung ihre
   Kennzeichnung nicht verliert, waehrend sie angesprungen ist. */
.vb-row--focus .vb-card { box-shadow:0 0 0 2px rgba(146,44,87,.55); }
.vb-countdown { font-family:${FONT_MONO}; font-size:11px; letter-spacing:.12em; text-transform:uppercase;
  color:${BERRY}; margin:0 0 6px; }
.vb-title--next { font-size:21px; }
/* min-width:0 und **kein** flex-shrink:0 (keine Backticks in diesem Block — er steht in einem
   Template-Literal): sonst ist die Flex-Basis die volle Breite beider Knoepfe nebeneinander,
   und ihr eigenes flex-wrap greift nie. */
.vb-card .vb-actions { margin-top:0; min-width:0; }
/* --- Die Zeile der Terminliste: drei Zonen untereinander. ---
   Kopf (was ist das fuer ein Event) · Band (was bringt es, bzw. was fehlt) · Knopfzeile (was
   kann ich tun). Vorher standen die Knoepfe rechts neben dem Titel; sobald die Zeile ein Band
   trug, war die Karte doppelt so hoch und die Handlung stand ueber ihrer Begruendung.
   Das Band laeuft randlos bis an die Kartenkante — es ist eine eigene Flaeche, keine Einlage;
   deshalb traegt die Karte selbst kein Padding mehr, sondern jede Zone ihr eigenes. Nur hier,
   nicht an .vb-card allgemein: dieselbe Klasse baut auch die Karten in *Eingereicht*, im
   Premium-Zustand und auf der Auswahlseite. */
.vb-row .vb-card { display:block; padding:0; overflow:hidden; }
.vb-row .vb-card-main { padding:15px 18px; }
.vb-row .vb-actions { margin:0; padding:12px 18px; border-top:1px solid #F1ECEE; }
.vb-card-wide { flex:1 0 100%; }
.vb-card-wide:empty { display:none; }
/* Randlos: das Band stoesst links und rechts an die Karte, oben trennt es eine Haarlinie vom
   Kopf. Unten braucht es keine — die Knopfzeile bringt ihre eigene mit. */
.vb-row .vb-hook, .vb-row .vb-metrics { margin:0; border-radius:0; border-left:0; border-right:0;
  border-bottom:0; }
.vb-row .vb-hook { padding:12px 18px 13px; }
/* Das Kennzahlenfeld hat kein eigenes Padding — es steckt in Kopfzeile und Wertespalten. Damit
   die Werte auf derselben Kante stehen wie Titel und Knoepfe, wandern die 18px dorthin. */
.vb-row .vb-metrics-head { padding-left:18px; padding-right:18px; }
.vb-row .vb-metric { padding-left:18px; padding-right:18px; }
.vb-row .vb-metric + .vb-metric { padding-left:13px; }
.vb-card:hover { border-color:#D6CFCF; box-shadow:0 2px 14px -8px rgba(44,44,44,.28); }
.vb-title { font-family:${FONT_HEAD}; font-weight:800; font-size:18px; color:${INK}; margin:0; line-height:1.2; }
.vb-meta { font-size:14px; color:${MUTED}; margin:6px 0 0; }

/* Strecken im Ton einer Ausschreibung — so schreibt ein Verein sie selbst auf. */
.vb-routes { font-family:${FONT_MONO}; font-size:12px; letter-spacing:.04em; color:${INK}; margin:10px 0 0; }
.vb-routes span { color:${MUTED}; }

/* Offene Buchungsanfrage — dieselbe Bauform wie die Wirkungszahlen, nur in Wartefarbe. */
.vb-pending { font-size:13.5px; color:${INK}; margin:10px 0 0; padding:9px 11px; line-height:1.5;
  background:rgba(240,195,106,.18); border-radius:9px; }
.vb-linkish { font:inherit; font-size:13.5px; color:${BERRY}; background:none; border:0; padding:0;
  margin-left:6px; cursor:pointer; text-decoration:underline; }

/* Wirkung der laufenden Hervorhebung. Steht unter dem Event, nicht daneben: es ist eine Auskunft
   über diesen Termin, keine Eigenschaft von ihm. */
.vb-promo { font-size:13.5px; color:${INK}; margin:10px 0 0; padding:9px 11px; line-height:1.5;
  background:rgba(146,44,87,.06); border-radius:9px; }
.vb-promo strong { font-family:${FONT_MONO}; font-size:13px; }
.vb-promo span { color:${MUTED}; }

.vb-chip { display:inline-flex; align-items:center; flex-shrink:0; font-family:${FONT_MONO}; font-size:10px;
  letter-spacing:.06em; text-transform:uppercase; padding:5px 9px; border-radius:6px; white-space:nowrap; }

.vb-actions { display:flex; flex-wrap:wrap; gap:8px; margin-top:16px; }
/* „Als Entwurf speichern" im Portal-Formular: Haken, Wort, ein Satz darunter. */
.vb-draft-toggle { display:flex; align-items:flex-start; gap:10px; padding:12px 14px; margin-bottom:14px;
  border:1px dashed ${BORDER}; border-radius:10px; font-size:14px; color:${INK}; cursor:pointer; }
.vb-draft-toggle input { margin-top:3px; }
.vb-draft-toggle-hint { display:block; margin-top:2px; font-size:13px; color:${MUTED}; line-height:1.45; }
.vb-btn { font-family:inherit; font-size:14px; font-weight:700; border-radius:10px; padding:10px 15px;
  cursor:pointer; border:1px solid ${BORDER}; background:#fff; color:${INK}; transition:background .16s, border-color .16s; }
.vb-btn:hover:not(:disabled) { border-color:#C9C2C2; background:#FBFAFA; }
.vb-btn:disabled { background:#EDE9E9; border-color:${BORDER}; color:${MUTED}; cursor:default; }
.vb-btn--primary { background:${BERRY}; border-color:${BERRY}; color:#fff; }
.vb-btn--primary:hover:not(:disabled) { background:#7E2549; border-color:#7E2549; }
.vb-btn--berry { border-color:${BERRY}; color:${BERRY}; }
.vb-btn--berry:hover:not(:disabled) { background:rgba(146,44,87,.06); border-color:${BERRY}; }
/* Nebenaktion in der Zeile: als Text, nicht als vierter gerahmter Knopf untereinander. */
.vb-link { font-family:inherit; font-size:14px; font-weight:700; color:${BERRY}; background:none;
  border:0; padding:10px 6px; cursor:pointer; border-radius:8px; }
.vb-link:hover { text-decoration:underline; }
.vb-link:focus-visible { outline:2px solid ${BERRY}; outline-offset:2px; }
.vb-btn:focus-visible, .vb-tab:focus-visible, .vb-switch select:focus-visible { outline:2px solid ${BERRY}; outline-offset:2px; }

/* --- Reiter: einzelne Pillen, jede mit eigenem Rand. ---
   Zwei Vorgaenger: erst eine Leiste aus 11px-Versalien mit 2px-Unterschnitt (zu leise — sie sah
   aus wie eine Beschriftung), dann ein Segment-Umschalter auf grauem Traeger. Der scheiterte am
   **Umbruch**: ein Segment-Umschalter behauptet eine durchgehende Leiste, und sobald die auf dem
   Phone in zwei Zeilen zerfaellt, bleiben drei graue Woerter und ein weisser Kasten uebrig — der
   Traeger sagt dann nichts mehr, und die inaktiven Reiter sind von Fliesstext nicht zu
   unterscheiden. Jede Pille traegt ihren Zustand jetzt selbst: weisse Flaeche, eigener Rand,
   der aktive dunkel gefuellt. Das haelt in einer Zeile wie in dreien.
   Umbruch bleibt richtig, Seitwaerts-Scrollen waere es nicht: ein gescrollter Reiter versteckt
   auf dem Phone ausgerechnet den, der etwas verkauft. */
.vb-tabs { display:flex; flex-wrap:wrap; gap:8px; margin:30px 0 26px; }
.vb-tab { display:inline-flex; align-items:center; gap:7px; font-family:inherit; font-size:14.5px;
  font-weight:700; background:#fff; border:1px solid ${BORDER}; border-radius:999px; color:#5C5C5C;
  padding:9px 17px; cursor:pointer; white-space:nowrap;
  transition:background .16s, color .16s, border-color .16s; }
.vb-tab:hover { color:${INK}; border-color:#C9C2C2; }
/* Gefuellt statt nur umrandet: „hier bin ich" muss sich von „das kannst du auch anklicken"
   unterscheiden, und zwar auf einen Blick und ohne Farbsehen. */
.vb-tab--on { background:${INK}; border-color:${INK}; color:#fff; }
.vb-tab--on:hover { color:#fff; border-color:${INK}; }
/* Die Zahl ist fuer Vorlesegeraete ausgeblendet (sonst „Eingereicht2") — der Knopf traegt sie im
   aria-label. Auf der dunklen Pille wuerde Beere auf Schwarz verschwinden: dort eine
   durchscheinende weisse Flaeche. */
.vb-tab-count { font-family:${FONT_MONO}; font-size:11px; font-weight:700; color:#fff; background:${BERRY};
  border-radius:999px; padding:2px 7px; line-height:1.3; }
.vb-tab--on .vb-tab-count { background:rgba(255,255,255,.24); }

/* --- Kennzahlenfeld einer laufenden Hervorhebung. ---
   Vorher ein Fliesstext mit fetten Zahlen darin; drei ausgerichtete Werte beantworten dieselbe
   Frage schneller. Die dritte Spalte traegt die Markenfarbe: sie ist der Grund, warum jemand
   bucht. auto-fit statt drei fester Spalten — sonst faellt genau diese Spalte auf dem Phone
   aus der Karte. */
.vb-metrics { margin-top:12px; border:1px solid #EADCE3; border-radius:12px; overflow:hidden; }
.vb-metrics-head { display:flex; flex-wrap:wrap; align-items:baseline; gap:4px 10px; padding:9px 13px;
  background:rgba(146,44,87,.07); }
.vb-metrics-head strong { font-family:${FONT_MONO}; font-weight:700; font-size:10.5px; letter-spacing:.1em;
  text-transform:uppercase; color:${BERRY}; }
.vb-metrics-head span { font-size:13px; color:${MUTED}; }
.vb-metrics-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(92px,1fr)); background:#fff; }
.vb-metric { padding:11px 13px; }
.vb-metric + .vb-metric { border-left:1px solid #F0EAEC; }
.vb-metric b { display:block; font-family:${FONT_HEAD}; font-weight:800; font-size:21px; line-height:1;
  font-variant-numeric:tabular-nums; color:${INK}; }
.vb-metric span { display:block; font-family:${FONT_MONO}; font-size:9.5px; letter-spacing:.1em;
  text-transform:uppercase; color:${MUTED}; margin-top:6px; }
.vb-metric--out b, .vb-metric--out span { color:${BERRY}; }

/* --- Premium-Haken: die Stellen, an denen eine Leistung FEHLT. ---
   Alle sechs sitzen dort, wo die Funktion waere, und nicht in einem Werbebanner: der leere Platz
   ist die Erklaerung. Gestrichelter Rand plus Beerenflaeche ist ueberall dieselbe Marke — wer sie
   einmal gelesen hat, erkennt sie beim naechsten Mal, ohne den Text zu lesen.
   Sie tragen alle dieselbe Aktion (den Premium-Dialog); es gibt keine zweite Erklaerseite. */
.vb-hook { margin-top:12px; border:1px dashed #DCC6D1; border-radius:12px; background:rgba(146,44,87,.05);
  padding:12px 14px; }
.vb-hook-kicker { font-family:${FONT_MONO}; font-size:10.5px; letter-spacing:.1em; text-transform:uppercase;
  color:${BERRY}; }
.vb-hook-text { font-size:13.5px; color:${INK}; margin:0; line-height:1.55; max-width:54ch; }
/* Satz und Namen links, Knopf rechts an der Kartenkante — zwei Spalten, nicht drei Zeilen.
   Als eigene Zeile bestimmte der Knopf mit seinen 38px die Hoehe zwischen Satz und Namen und
   riss ein Loch in die Flaeche; daneben kostet er gar keine. Oben ausgerichtet, damit er bei
   zweizeiligem Satz nicht auf halber Hoehe schwebt. */
/* „Neuer Termin fehlt" benutzt dieselbe Form, aber nicht denselben Ton: hier wird nichts
   verkauft, hier fehlt etwas an der eigenen Arbeit. Rost statt Amber, weil Amber in kleiner
   Schrift auf hell nur rund 2:1 erreicht (siehe Designsystem). */
.vb-hook--todo { border-color:#E3CDBB; background:rgba(140,59,11,.05); }
.vb-hook--todo .vb-hook-kicker { color:#8C3B0B; }
.vb-hook-row { display:flex; flex-wrap:wrap; align-items:flex-start; gap:10px 22px; margin-top:6px; }
.vb-hook-copy { flex:1 1 min(34ch,100%); }
.vb-hook-row .vb-btn { margin-left:auto; font-size:13.5px; padding:9px 14px; white-space:nowrap; }
.vb-hook-metric b { display:block; font-family:${FONT_HEAD}; font-weight:800; font-size:21px; line-height:1;
  font-variant-numeric:tabular-nums; color:${INK}; }
.vb-hook-metric span { display:block; font-family:${FONT_MONO}; font-size:9.5px; letter-spacing:.1em;
  text-transform:uppercase; color:${MUTED}; margin-top:6px; }
/* Verdeckt statt weggelassen: dass es die Zahl GIBT, ist die halbe Auskunft.
   Bewusst KEINE erfundenen Ziffern hinter dem Weichzeichner (der Entwurf liess beides zu) — ein
   ausgedachter Wert steht im DOM und liest sich fuer jeden, der hinsieht, wie eine Behauptung
   ueber dieses Event. Der Platzhalter ist deshalb ein Zeichen ohne Aussage. */
.vb-hook-metric--locked b { filter:blur(6px); color:${BERRY}; user-select:none; }

/* Gesperrte Aktion: gleiche Geometrie wie die uebrigen Knoepfe, aber gestrichelt und in Beere.
   NICHT disabled — er fuehrt in den Dialog. Ein toter Knopf erklaert nichts. */
.vb-btn--locked { border-style:dashed; border-color:#DCC6D1; background:rgba(146,44,87,.05); color:${BERRY};
  display:inline-flex; align-items:center; gap:7px; }
.vb-btn--locked:hover:not(:disabled) { background:rgba(146,44,87,.1); border-color:#DCC6D1; }

/* --- Reiter *Sichtbarkeit & Promotion*: Abschnitte, Zeilen, Verkaufskarte. ---
   Der Reiter bestand frueher aus einer einzigen Sperrkarte. Jetzt traegt er vier Abschnitte, und
   die brauchen eine ruhige, wiederkehrende Zeilenform — sonst wird aus vier Auskuenften eine
   Wand aus Kaesten. Deshalb: Ueberschrift in Versalien, ein Satz darunter, dann Zeilen. */
.vb-section-lead { font-size:14px; color:${MUTED}; margin:0 0 12px; max-width:62ch; }

.vb-features { display:grid; gap:10px; }
.vb-feature { display:flex; flex-wrap:wrap; align-items:center; justify-content:space-between;
  gap:14px; background:#fff; border:1px solid ${BORDER}; border-radius:14px; padding:14px 16px; }
.vb-feature-main { min-width:min(240px,100%); flex:1; }
/* Zwei Knoepfe an der Zeile: kaufen und hinsehen. Dieselbe Rangfolge wie am Termin — der
   gefuellte fuehrt, der umrandete steht daneben. */
.vb-feature-actions { display:flex; flex-wrap:wrap; gap:8px; }
.vb-feature-head { display:flex; flex-wrap:wrap; align-items:center; gap:9px; }
.vb-feature-head strong { font-family:${FONT_HEAD}; font-weight:800; font-size:15.5px; color:${INK}; }
.vb-feature p { font-size:13.5px; color:${MUTED}; margin:5px 0 0; line-height:1.5; }
/* Der Zustand als Wort, nicht als Farbe allein: „Steht" und „Fehlt noch" sind zwei Auskuenfte,
   und ein gruener Punkt waere fuer die eine wie die andere lesbar. */
.vb-feature-state { font-family:${FONT_MONO}; font-size:9.5px; letter-spacing:.08em;
  text-transform:uppercase; color:${LABEL}; background:#F2EEEE; border-radius:6px; padding:4px 8px; }
.vb-feature-state--on { color:${GREEN}; background:rgba(31,138,91,.1); }
/* Haken statt Wortmarke an erledigten Sachen. Gefuellte Scheibe wie am Schloss (.vb-lockbadge),
   nur gruen und etwas kleiner: „steht" ist eine Bestaetigung, keine Sperre, und soll die Zeile
   nicht anfuehren. line-height:0 am Traeger, display:block am Bild — sonst rechnet die Zeile die
   Schriftlinie mit und das Zeichen sitzt ein halbes Pixel zu hoch. */
.vb-checkbadge { display:inline-flex; align-items:center; justify-content:center; flex:0 0 18px;
  width:18px; height:18px; border-radius:50%; background:${GREEN}; color:#fff; line-height:0; }
.vb-checkbadge svg { display:block; }
/* Wartend — dieselbe Farbe wie „In Pruefung" am Event und wie die Marke am Bild oben im Kopf. */
.vb-feature-state--pending { color:#8A6510; background:rgba(217,159,26,.16); }

/* Verkaufskarte. Hell wie der Rest des Bereichs; laut wird sie ueber Groesse, Markenfarbe und
   den abgesetzten Preisblock — nicht ueber dunklen Grund. Der dunkle Entwurf davor war eine
   zweite Farbwelt mitten in einer hellen Seite.
   Aufbau: Text links, Preis rechts, Leistungen als Fuss ueber die volle Breite. */
.vb-sell { display:grid; grid-template-columns:minmax(0,1fr) minmax(230px,auto);
  gap:26px 30px; align-items:start; background:#fff; border:1px solid ${BORDER};
  border-radius:20px; padding:26px 28px; box-shadow:0 10px 30px -18px rgba(44,44,44,.35); }
.vb-sell-badge { display:inline-flex; align-items:center; font-family:${FONT_MONO}; font-size:10px;
  font-weight:700; letter-spacing:.14em; text-transform:uppercase; color:#fff; background:${BERRY};
  border-radius:7px; padding:5px 10px; }
.vb-sell-title { font-family:${FONT_HEAD}; font-weight:800; font-size:clamp(24px,3.4vw,32px);
  letter-spacing:-.02em; color:${INK}; margin:14px 0 12px; line-height:1.12; }
.vb-sell-title em { font-style:normal; color:${BERRY}; }
.vb-sell-proof { font-size:15px; color:${INK}; margin:0 0 14px; line-height:1.55; max-width:54ch; }
.vb-sell-proof b { font-family:${FONT_HEAD}; font-weight:800; font-size:21px; color:${BERRY};
  font-variant-numeric:tabular-nums; margin-right:3px; }
/* Der Anlass: Datum fett, Folge daneben. Mit Symbol, damit er als Hinweis lesbar ist und nicht
   als weiterer Satz. */
.vb-sell-urgent { display:flex; gap:12px; align-items:flex-start; background:rgba(146,44,87,.06);
  border-radius:12px; padding:12px 14px; max-width:56ch; }
.vb-sell-urgent-icon { flex:0 0 34px; width:34px; height:34px; border-radius:50%; background:#fff;
  color:${BERRY}; display:flex; align-items:center; justify-content:center; }
/* Nur der Textblock: ein nacktes „span" traf auch die Kreis-Huelle des Symbols und machte aus
   ihrem display:flex ein display:block, das Symbol hing dann oben links im Kreis. */
.vb-sell-urgent div > strong { display:block; font-size:14.5px; color:${INK}; }
.vb-sell-urgent div > span { display:block; font-size:13.5px; color:${MUTED}; margin-top:2px; }

/* Preisblock: eigener Rahmen, damit der Preis nicht im Text mitschwimmt. */
.vb-sell-side { display:flex; flex-direction:column; gap:8px; text-align:center; padding:18px 18px 16px;
  border:1px solid ${BORDER}; border-radius:16px; background:#FBFAFA; }
/* Die Trennlinie sass an „Alle Vorteile inklusive" (raus am 02.09.2026 — sie stand ueber einer
   Reihe, die genau diese Vorteile aufzaehlt) und haengt jetzt am Preis selbst: sie trennt Betrag
   und Knopf, nicht die beiden Saetze. */
.vb-sell-price { font-family:${FONT_HEAD}; font-weight:800; font-size:21px; color:${BERRY};
  margin:0 0 4px; padding-bottom:13px; border-bottom:1px solid ${BORDER};
  white-space:nowrap; letter-spacing:-.01em; }
/* Preis und Steuerangabe gehoeren zusammen, also traegt der Block die Trennlinie und nicht mehr
   der Betrag allein — sonst stuende „zzgl. 19 % MwSt." unterhalb der Linie beim Knopf. */
.vb-sell-priceblock { padding-bottom:13px; margin-bottom:4px; border-bottom:1px solid ${BORDER}; }
.vb-sell-priceblock .vb-sell-price { margin:0; padding-bottom:0; border-bottom:0; }
.vb-sell-vat { font-size:12.5px; color:${LABEL}; margin:3px 0 0; }
.vb-sell-cta { background:${BERRY}; border-color:${BERRY}; color:#fff; white-space:nowrap;
  padding:12px 18px; }
.vb-sell-cta:hover:not(:disabled) { background:#7E2549; border-color:#7E2549; }
/* Liegt die Anfrage schon bei uns, ist jeder Premium-Knopf keiner mehr, sondern der Zustand:
   ausgegraut und tot (siehe PremiumCta). Ein zweiter Kauf ist ohnehin nicht moeglich, der Server
   weist ihn ab (countOpenVerificationsForOrganizer). Das Ausgrauen steht deshalb an .vb-btn
   selbst und nicht an einer einzelnen Verwendung. */

/* Leistungen als Fuss: **eine Reihe, vier Spalten, je Bild, Name, ein Satz.** Vorher drei
   linksbuendige Bloecke mit Werbeueberschrift und drei- bis vierzeiligem Absatz — nebeneinander
   war das eine Textwand, und einer der vier stand allein in der zweiten Reihe.
   Mittig und durch gestrichelte Linien getrennt: vier gleichwertige Sachen, keine Aufzaehlung mit
   Rangfolge. Die Trennlinien liegen am Element (nicht als eigene Spalte), damit die Reihe beim
   Umbruch nicht auseinanderfaellt. */
.vb-sell-perks { grid-column:1 / -1; display:grid; gap:0;
  grid-template-columns:repeat(auto-fit,minmax(160px,1fr)); border-top:1px solid ${BORDER};
  padding-top:18px; }
.vb-sell-perk { text-align:center; padding:2px 14px; }
.vb-sell-perk + .vb-sell-perk { border-left:1px dashed #E7E1E1; }
.vb-sell-perk-icon { display:flex; align-items:center; justify-content:center; width:34px;
  height:34px; border-radius:50%; background:rgba(146,44,87,.08); color:${BERRY};
  margin:0 auto 9px; }
.vb-sell-perk strong { display:block; font-family:${FONT_HEAD}; font-weight:800; font-size:13.5px;
  color:${INK}; line-height:1.25; }
/* Eigene Klasse statt eines Nachfahren-Selektors auf span: der Icon-Traeger ist ebenfalls ein
   span, und der
   Nachfahren-Selektor haette ihn mit display:block ueberschrieben — dann faellt das Zeichen aus
   seiner Kreisflaeche in die linke obere Ecke. Keine Element-Selektoren dort, wo zwei gleiche
   Elemente verschiedene Aufgaben haben. */
.vb-sell-perk-text { display:block; font-size:12.5px; color:${MUTED}; margin-top:4px; line-height:1.45; }
@media (max-width:760px) {
  .vb-sell { grid-template-columns:1fr; padding:22px 20px; }
  /* Zwei Spalten statt vier, und ohne Trennlinien: umgebrochen stuende sonst am ersten Element
     der zweiten Reihe eine Linie ins Leere. */
  .vb-sell-perks { grid-template-columns:repeat(2,minmax(0,1fr)); gap:18px 0; }
  .vb-sell-perk + .vb-sell-perk { border-left:0; }
}

/* Der Moment nach dem Freischalten. Hell und in Markenfarbe, nicht dunkel: das hier ist kein
   Angebot mehr, sondern eine Quittung. Sie verschwindet nach einer Woche von selbst. */
.vb-welcome { background:rgba(146,44,87,.06); border:1px solid #DCC6D1; border-radius:16px;
  padding:18px 20px; margin-bottom:18px; }
.vb-welcome-title { font-family:${FONT_HEAD}; font-weight:800; font-size:19px; color:${INK};
  margin:6px 0 8px; line-height:1.2; }
.vb-welcome p { font-size:14px; color:${INK}; margin:0; line-height:1.55; max-width:60ch; }

/* Reichweite: die eine echte Zahl gross, der Satz daneben. */
.vb-reach { background:#fff; border:1px solid ${BORDER}; border-radius:14px; padding:16px 18px;
  display:flex; flex-wrap:wrap; align-items:center; gap:16px 22px; }
.vb-reach-count b { display:block; font-family:${FONT_HEAD}; font-weight:800; font-size:30px;
  line-height:1; font-variant-numeric:tabular-nums; color:${BERRY}; }
/* Null ist keine Leistung: in Beere liest sich die grosse Ziffer wie ein Erfolg. */
.vb-reach-count--none b { color:#C9C2C2; }
.vb-reach-count span { display:block; font-family:${FONT_MONO}; font-size:9.5px; letter-spacing:.1em;
  text-transform:uppercase; color:${MUTED}; margin-top:6px; }
.vb-reach-text { flex:1 1 260px; max-width:52ch; font-size:14px; color:${INK}; margin:0;
  line-height:1.55; }

/* Karte ueber einem gesperrten Bereich (Reiter Zahlen, Reiter Sichtbarkeit). */
.vb-lockcard { display:flex; flex-wrap:wrap; align-items:center; gap:14px 20px; background:#fff;
  border:1px dashed #DCC6D1; border-radius:16px; padding:17px 19px; margin-bottom:22px; }
.vb-lockcard-main { min-width:min(260px,100%); flex:1; }
.vb-lockcard p { font-size:14.5px; color:${INK}; margin:7px 0 0; line-height:1.55; max-width:58ch; }
.vb-lockcard .vb-btn { padding:11px 16px; white-space:nowrap; }


/* Vorschau eines gesperrten Bereichs. Der Inhalt darunter ist ein Geruest ohne Werte — aus
   demselben Grund wie oben: eine unscharfe Tabelle mit erfundenen Zahlen waere eine Aussage. */
.vb-blurred { filter:blur(4.5px); opacity:.5; pointer-events:none; user-select:none; }
.vb-skel { height:13px; border-radius:4px; background:linear-gradient(90deg,#E5E1E1,#EFEAEA); }

/* Pille im Pflege-Balken, solange nichts gebucht ist ("Basis-Zugang" / "Premium angefragt").
   Dieselbe Geometrie wie .om-premium in PremiumStatus — die beiden stehen nie zugleich da,
   sollen aber gleich aussehen, wenn man zwischen den Zustaenden wechselt. */
.om-plan { font-size:12.5px; font-weight:700; white-space:nowrap; border-radius:999px; padding:4px 10px;
  background:rgba(255,255,255,.72); color:${LABEL}; }
.om-unlock { display:inline-flex; align-items:center; gap:7px; font-family:inherit; font-size:12.5px;
  font-weight:700; border-radius:8px; padding:7px 12px; cursor:pointer; border:1px solid ${BERRY};
  background:${BERRY}; color:#fff; margin-left:auto; }
.om-unlock:hover { background:#7E2549; border-color:#7E2549; }
.om-unlock:focus-visible { outline:2px solid ${BERRY}; outline-offset:2px; }
@media (max-width:520px) { .om-unlock { margin-left:0; } }
/* --- Krume ueber dem Briefkopf: der Weg ins Verzeichnis, und der einzige. ---
   Zwei Anlaeufe daneben sind wieder raus: ein Link im Pflege-Balken (der verkauft und verortet,
   er navigiert nicht) und ein ausgeschriebenes „Zu allen Veranstaltern" rechts in derselben
   Zeile — zwei Wege zum selben Ziel nebeneinander sind einer zu viel. Gesehen wird der eine
   ueber die Farbe, nicht ueber ein zweites Wort. */
.vb-crumb { display:flex; flex-wrap:wrap; align-items:baseline; gap:4px;
  font-family:${FONT_MONO}; font-size:12px; letter-spacing:.06em; color:#6A6A6A; margin:0 0 18px; }
/* Der anklickbare Teil in der Akzentfarbe: grau war er von der Pfadangabe daneben nicht zu
   unterscheiden, und dass die Zeile ueberhaupt irgendwohin fuehrt, hat niemand gesehen. */
.vb-crumb a { color:${BERRY}; text-decoration:none; }
.vb-crumb a:hover { text-decoration:underline; }
.vb-crumb a:focus-visible { outline:2px solid ${BERRY}; outline-offset:3px; border-radius:3px; }
/* Der Name kann lang sein; er darf die Zeile fuellen und abschneiden. */
.vb-crumb-here { color:${INK}; min-width:0; overflow:hidden; text-overflow:ellipsis;
  white-space:nowrap; }

/* --- Statistiken: Kacheln fuer die Summen, Balken fuer den Vergleich. ---
   Die Kacheln beantworten "wie viel insgesamt", die Tabelle darunter "welches Event". Beides in
   einer Zeile Fliesstext zu sagen, wie es bis zum 01.09.2026 der Fall war, machte aus vier
   Zahlen einen Satz, den man liest statt ueberfliegt. */
.vb-stats { display:grid; grid-template-columns:repeat(auto-fit,minmax(140px,1fr)); gap:10px;
  margin:0 0 18px; }
.vb-stat { background:#fff; border:1px solid ${BORDER}; border-radius:14px; padding:14px 16px; }
.vb-stat b { display:block; font-family:${FONT_HEAD}; font-weight:800; font-size:26px; line-height:1;
  font-variant-numeric:tabular-nums; color:${INK}; }
.vb-stat span { display:block; font-family:${FONT_MONO}; font-size:9.5px; letter-spacing:.1em;
  text-transform:uppercase; color:${LABEL}; margin-top:8px; }
/* Die Spalte, an der die Leistung gemessen wird — dieselbe Auszeichnung wie in der Tabelle. */
.vb-stat--out { border-color:#EADCE3; }
.vb-stat--out b, .vb-stat--out span { color:${BERRY}; }

/* Namen gesperrter Kennzahlen am Termin. Kein Zahlenplatz, kein Weichzeichner: nur die Auskunft,
   WAS es zu sehen gaebe — und darueber, warum nicht.

   In einer Zeile und in derselben Beschriftung wie bei .vb-metric span: mit Premium stehen genau
   diese drei Woerter an genau dieser Stelle, nur mit Zahlen darueber. Der leere Platz ist damit
   die Erklaerung. Als Pillenreihe ohne Schloesser waeren es Schlagworte gewesen — die gefuellte
   Beerenscheibe vor jedem Wort traegt den Zustand, nicht eine duenne Kontur. Die frueher noetige
   Ueberschrift „Nur mit Premium" faellt damit weg: der Satz darueber sagt es schon. */
.vb-locks { list-style:none; display:flex; flex-wrap:wrap; align-items:center; gap:8px 18px;
  margin:9px 0 0; padding:0; }
.vb-locks li { display:inline-flex; align-items:center; gap:8px; font-family:${FONT_MONO};
  font-size:10px; letter-spacing:.1em; text-transform:uppercase; color:${MUTED}; }
/* Gefuellte Scheibe statt Kontur: auf dieser Groesse traegt nur eine Flaeche die Form.
   line-height:0 und display:block am Bild: sonst rechnet die Zeile die Schriftlinie mit ein und
   das Zeichen sitzt ein halbes Pixel zu hoch. Die 12px des Icons sind bewusst gerade — bei 20px
   Scheibe bleiben so 4px auf jeder Seite statt 4,5. */
.vb-lockbadge { display:inline-flex; align-items:center; justify-content:center; flex:0 0 20px;
  width:20px; height:20px; border-radius:50%; background:${BERRY}; color:#fff; line-height:0; }
.vb-lockbadge svg { display:block; }

/* --- Zahlen: Balken statt Kachelreihe. Vergleichbarkeit ist hier die Auskunft. --- */
.vb-figures { font-family:${FONT_MONO}; font-size:12px; letter-spacing:.04em; color:${INK}; margin:0 0 6px; }
.vb-table { width:100%; border-collapse:collapse; font-size:14px; font-variant-numeric:tabular-nums; }
/* Die Spalte, an der die Leistung gemessen wird — im Kopf wie im Wert. */
.vb-table th.vb-col-out { color:${BERRY}; }
.vb-table td.vb-col-out { font-weight:700; }
.vb-table th { font-family:${FONT_MONO}; font-size:10.5px; letter-spacing:.1em; text-transform:uppercase;
  color:${LABEL}; font-weight:400; text-align:right; padding:0 0 10px 14px; }
.vb-table th:first-child { text-align:left; padding-left:0; }
.vb-table td { padding:11px 0 11px 14px; border-top:1px solid ${BORDER}; text-align:right; color:${INK}; }
.vb-table td:first-child { text-align:left; padding-left:0; }
.vb-bar { position:relative; }
.vb-bar::before { content:''; position:absolute; right:0; top:50%; transform:translateY(-50%);
  height:22px; background:rgba(146,44,87,.10); border-radius:3px; width:var(--w); }
.vb-bar > span { position:relative; }

/* Fragezeichen an einer Kennzahl. Dezent, aber ein echter Knopf — es muss sichtbar sein, dass es
   dort etwas zu holen gibt, sonst haette man es auch weglassen koennen. */
.vb-hint-btn { display:inline-flex; align-items:center; justify-content:center; width:14px;
  height:14px; margin-left:5px; padding:0; vertical-align:middle; border-radius:50%;
  border:1px solid #C9C2C2; background:transparent; color:${LABEL}; font-family:${FONT_MONO};
  font-size:9px; line-height:1; cursor:pointer; transition:color .14s, border-color .14s; }
.vb-hint-btn:hover { color:${BERRY}; border-color:${BERRY}; }
.vb-hint-btn:focus-visible { outline:2px solid ${BERRY}; outline-offset:2px; }
/* Fest am Sichtfenster positioniert (position:fixed), damit der overflow-x:auto-Container der
   Tabelle nichts abschneidet — die Koordinaten rechnet die Komponente aus der Knopfposition.
   (Keine Backticks in diesem Block: er steht in einem Template-Literal und wuerde es beenden.) */
.vb-hint-pop { position:fixed; z-index:60; width:250px; box-sizing:border-box; padding:11px 13px;
  border-radius:11px; background:#2C2C2C; font-family:'Hanken Grotesk',sans-serif; color:#F4EEEA;
  font-size:13px; font-weight:400; line-height:1.5; letter-spacing:0; text-transform:none;
  text-align:left; box-shadow:0 12px 30px -12px rgba(44,44,44,.6); }
.vb-hint-pop strong { display:block; font-family:${FONT_MONO}; font-size:9.5px; letter-spacing:.1em;
  text-transform:uppercase; color:#E4B9CC; margin-bottom:5px; }

/* Der Event-Titel in der Zahlen-Tabelle fuehrt auf die oeffentliche Seite. Wie im Briefkopf:
   sieht aus wie Text, bis man ihn beruehrt. */
.vb-event-link { color:${INK}; text-decoration:none; border-bottom:1px solid #D6CFCF; }
.vb-event-link:hover { color:${BERRY}; border-bottom-color:${BERRY}; }
.vb-event-link:focus-visible { outline:2px solid ${BERRY}; outline-offset:2px; border-radius:3px; }

/* Ein Vorgeschmack: die eine Zahl gross, der Satz daneben. Kein Kasten mit Rahmen — er steht
   direkt unter der Sperrkarte und waere sonst ein zweiter Kasten am selben Ort. */
.vb-teaser { display:flex; align-items:baseline; gap:12px; margin:0 0 8px; max-width:64ch; }
.vb-teaser b { font-family:${FONT_HEAD}; font-weight:800; font-size:30px; line-height:1;
  color:${BERRY}; font-variant-numeric:tabular-nums; }
.vb-teaser span { font-size:14px; color:${INK}; line-height:1.55; }

.vb-empty { background:#fff; border:1px dashed #D6CFCF; border-radius:16px; padding:28px; text-align:center; }
/* Der Pruefhinweis steht am rechten Rand der Werkzeugzeile — auf dem Phone waere das eine
   einsame, rechtsbuendige Zeile, dort laeuft er normal mit. */
.vb-toolbar { display:flex; flex-wrap:wrap; gap:14px; align-items:center; margin-bottom:4px; }
/* Datum in der Karte — nur auf dem Phone, wo die Zeitachse daneben entfaellt. Siehe .vb-node. */
.vb-when { display:none; }

/* Briefkopf: die Vereinsangaben stehen unter dem Namen wie auf einem Briefbogen — sie gehoeren
   zur Identitaet, nicht in einen eigenen Reiter. */
.vb-letterhead { display:flex; flex-wrap:wrap; align-items:flex-start; justify-content:space-between;
  gap:18px; border-top:1px solid ${BORDER}; border-bottom:1px solid ${BORDER}; padding:16px 0; margin:0 0 30px; }
.vb-defs { display:grid; grid-template-columns:max-content 1fr; gap:7px 22px; margin:0; }
.vb-defs dt { font-family:${FONT_MONO}; font-size:11px; letter-spacing:.08em; text-transform:uppercase;
  color:${LABEL}; padding-top:2px; }
.vb-defs dd { font-size:14px; color:${INK}; margin:0; }

@media (prefers-reduced-motion: no-preference) {
  .vb-row, .vb-next { animation:vb-in .38s cubic-bezier(.2,.7,.3,1) both; }
  @keyframes vb-in { from { opacity:0; transform:translateY(9px); } to { opacity:1; transform:none; } }
}
@media (max-width:700px) {
  .vb-card { flex-direction:column; align-items:stretch; gap:12px; }
  .vb-card .vb-actions { margin-top:0; }
  .vb-h1 { font-size:30px; }
  .vb-count { font-size:42px; }
  .vb-next { padding:20px; border-radius:16px; }

  /* --- Die Saison ohne Zeitachse. ---
     Die Spalte links kostete 40px plus 10px Abstand fuer drei Zeichen — auf einem 360px breiten
     Schirm ein Siebtel der Breite, und der Titel brach dafuer um. Das Datum steht deshalb in der
     Karte (.vb-when), die Achse faellt ganz weg: eine Linie zwischen Karten, die die volle
     Breite haben, verbindet nichts mehr, was nicht schon durch die Reihenfolge klar waere. */
  .vb-row { grid-template-columns:1fr; gap:0; padding:0 0 12px; }
  .vb-row::before, .vb-node, .vb-countdown { display:none; }
  .vb-when { display:flex; flex-wrap:wrap; align-items:center; gap:9px; margin:0 0 7px;
    font-family:${FONT_MONO}; font-size:11.5px; letter-spacing:.08em; color:${LABEL}; }
  /* Der Countdown als Pille am Datum statt als eigene Zeile darueber: er ist eine Angabe zu
     diesem Datum, keine Ueberschrift. */
  .vb-when-pill { font-family:${FONT_MONO}; font-size:10px; letter-spacing:.06em;
    text-transform:uppercase; color:${BERRY}; background:rgba(146,44,87,.09);
    border-radius:6px; padding:4px 8px; }
  .vb-month { margin:22px 0 10px; }
  .vb-toolbar { gap:10px 14px; }
  /* Der einzige Weg, ein Event anzulegen — auf dem Phone ueber die volle Breite, statt neben
     einer Ankreuzung zu stehen, die nur die Liste filtert. */
  .vb-toolbar .vb-btn--primary { width:100%; }
  .vb-day { font-size:17px; }

  /* Zwei gleich breite Knoepfe je Zeile statt einer Reihe, die nach Textlaenge umbricht: drei
     verschieden breite Kaesten in zwei Zeilen sahen aus wie drei verschieden wichtige Aktionen. */
  .vb-row .vb-actions { display:grid; grid-template-columns:1fr 1fr; gap:8px; }
  .vb-row .vb-actions .vb-btn { width:100%; }
  /* Satz, Namen und Knopf untereinander — nebeneinander bleiben fuer den Knopf keine 120px. */
  .vb-hook-copy { flex:1 1 100%; }
  .vb-hook-row .vb-btn { width:100%; margin-left:0; }
  /* Wie an der Terminzeile: zwei gleich breite Knoepfe statt zweier verschieden langer
     Kaesten, die nach Textlaenge umbrechen. */
  .vb-feature-actions { width:100%; display:grid; grid-template-columns:1fr 1fr; }
  .vb-feature-actions .vb-btn { width:100%; }
}
`;
